import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  useTheme,
  TextField,
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import {
  MenuItem,
  Chip,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon, History as HistoryIcon, GetApp as ExportIcon, FilterList as FilterIcon, Clear as ClearIcon, Visibility as VisibilityIcon } from '@mui/icons-material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useSnackbar } from 'notistack';
import { TimeEntry } from '../../types/api';
import * as timeEntriesAPI from '../../services/timeEntriesAPI';
import { ConfirmDialog } from '../../components/ConfirmDialog/ConfirmDialog';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';

interface HistoryEntry {
  id: number;
  user_id: number;
  change_type: 'create' | 'update' | 'delete';
  old_values: any;
  new_values: any;
  created_at: string;
}

interface FilterState {
  project_id: number | '';
  user_id: number | '';
  start_date: string;
  end_date: string;
}

export const TimeTrackingList: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();

  // Rollen-basierte Berechtigungen für Zeiterfassung
  const isAdmin = user?.role === 'admin';
  const isBauleiter = user?.role === 'bauleiter';
  const isManagement = user?.role === 'management';
  const isForeman = user?.role === 'foreman';
  const isInstaller = user?.role === 'installer';
  
  // Admin & Bauleiter können alle Zeiteinträge sehen
  const canSeeAll = isAdmin || isBauleiter;
  // Andere sehen nur eigene Zeiteinträge
  const canSeeRestricted = isManagement || isForeman || isInstaller;
  
  // Zeiterfassung-Berechtigungen:
  const canEditTimeEntries = isAdmin; // Nur Admin kann bearbeiten
  const canDeleteTimeEntries = isAdmin; // Nur Admin kann löschen
  const canCreateTimeEntries = isAdmin || isBauleiter; // Nur Admin & Bauleiter können erstellen
  const [entries, setEntries] = useState<TimeEntry[]>([]);
  const [selectedEntry, setSelectedEntry] = useState<TimeEntry | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [historyEntries, setHistoryEntries] = useState<HistoryEntry[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [projects, setProjects] = useState<{id: number, name: string}[]>([]);
  const [users, setUsers] = useState<{id: number, name: string}[]>([]);
  const [filters, setFilters] = useState<FilterState>({
    project_id: '',
    user_id: '',
    start_date: '',
    end_date: ''
  });

  const fetchEntries = async () => {
    try {
      const filterParams = {
        project_id: filters.project_id || undefined,
        user_id: filters.user_id || undefined,
        start_date: filters.start_date || undefined,
        end_date: filters.end_date || undefined
      };
      const data = await timeEntriesAPI.getEntries(filterParams);
      
      // Format the dates and times for display
      const formattedData = data.map(entry => ({
        ...entry,
        formatted_date: entry.start_time ? format(new Date(entry.start_time), 'dd.MM.yyyy', { locale: de }) : 
                       (entry.date ? format(new Date(entry.date), 'dd.MM.yyyy', { locale: de }) : ''),
        formatted_start_time: entry.start_time ? format(new Date(entry.start_time), 'HH:mm', { locale: de }) : '',
        formatted_end_time: entry.end_time ? format(new Date(entry.end_time), 'HH:mm', { locale: de }) : ''
      }));
      
      setEntries(formattedData);
    } catch (error) {
      console.error('Error fetching time entries:', error);
      enqueueSnackbar('Fehler beim Laden der Zeiteinträge', { variant: 'error' });
    }
  };

  const fetchProjects = async () => {
    try {
      const data = await timeEntriesAPI.getProjects();
      setProjects(data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const data = await timeEntriesAPI.getUsers();
      setUsers(data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  useEffect(() => {
    fetchProjects();
    fetchUsers();
  }, []);

  useEffect(() => {
    fetchEntries();
  }, [filters]);

  const handleDelete = async () => {
    if (!selectedEntry) return;

    try {
      await timeEntriesAPI.deleteEntry(selectedEntry.id);
      enqueueSnackbar('Zeiteintrag erfolgreich gelöscht', { variant: 'success' });
      setDeleteDialogOpen(false);
      setSelectedEntry(null);
      fetchEntries();
    } catch (error) {
      console.error('Error deleting time entry:', error);
      enqueueSnackbar('Fehler beim Löschen des Zeiteintrags', { variant: 'error' });
    }
  };

  const handleExport = async () => {
    try {
      // Convert filters to proper format for PDF export
      const exportFilters = {
        project_id: filters.project_id === '' ? undefined : Number(filters.project_id),
        user_id: filters.user_id === '' ? undefined : Number(filters.user_id),
        start_date: filters.start_date || undefined,
        end_date: filters.end_date || undefined
      };
      
      // Export only the filtered/displayed entries
      await timeEntriesAPI.exportTimeEntriesToPDF(entries, exportFilters);
      enqueueSnackbar('PDF-Report wurde erfolgreich erstellt', { variant: 'success' });
    } catch (error) {
      console.error('Error exporting time entries to PDF:', error);
      enqueueSnackbar('Fehler beim Erstellen des PDF-Reports', { variant: 'error' });
    }
  };

  const handleViewHistory = async (entry: TimeEntry) => {
    try {
      const history = await timeEntriesAPI.getTimeEntryHistory(entry.id);
      setHistoryEntries(history);
      setHistoryDialogOpen(true);
    } catch (error) {
      console.error('Error fetching time entry history:', error);
      enqueueSnackbar('Fehler beim Laden der Änderungshistorie', { variant: 'error' });
    }
  };

  const formatHistoryChange = (entry: HistoryEntry): string => {
    const formatTime = (time: string) => format(new Date(time), 'dd.MM.yyyy HH:mm', { locale: de });
    
    switch (entry.change_type) {
      case 'create':
        return `Eintrag erstellt: ${entry.new_values.description} (${formatTime(entry.new_values.start_time)} - ${formatTime(entry.new_values.end_time)})`;
      case 'update':
        const changes: string[] = [];
        if (entry.old_values.start_time !== entry.new_values.start_time) {
          changes.push(`Startzeit von ${formatTime(entry.old_values.start_time)} zu ${formatTime(entry.new_values.start_time)}`);
        }
        if (entry.old_values.end_time !== entry.new_values.end_time) {
          changes.push(`Endzeit von ${formatTime(entry.old_values.end_time)} zu ${formatTime(entry.new_values.end_time)}`);
        }
        if (entry.old_values.activity !== entry.new_values.activity) {
          changes.push(`Beschreibung geändert`);
        }
        return `Eintrag aktualisiert: ${changes.join(', ')}`;
      case 'delete':
        return `Eintrag gelöscht: ${entry.old_values.description} (${formatTime(entry.old_values.start_time)} - ${formatTime(entry.old_values.end_time)})`;
      default:
        return 'Unbekannte Änderung';
    }
  };

  const handleFilterChange = (field: keyof FilterState, value: any) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => {
    setFilters({
      project_id: '',
      user_id: '',
      start_date: '',
      end_date: ''
    });
  };

  const getActiveFiltersCount = () => {
    return Object.values(filters).filter(value => value !== '').length;
  };

  const baseColumns: GridColDef[] = [
    { field: 'formatted_date', headerName: 'Datum', width: 100 },
    { field: 'project_name', headerName: 'Projekt', width: 200 },
    { field: 'user_name', headerName: 'Mitarbeiter', width: 150 },
    { field: 'formatted_start_time', headerName: 'Startzeit', width: 150 },
    { field: 'formatted_end_time', headerName: 'Endzeit', width: 150 },
    { field: 'break_duration', headerName: 'Pause (Min)', width: 100 },
    { field: 'description', headerName: 'Beschreibung', width: 300 },
  ];

  const actionsColumn: GridColDef = {
    field: 'actions',
    headerName: 'Aktionen',
    width: 180,
    renderCell: (params) => (
      <Box>
        <IconButton onClick={() => navigate(`/time-tracking/${params.row.id}`)}>
          <VisibilityIcon />
        </IconButton>
        {canEditTimeEntries && (
          <IconButton onClick={() => navigate(`/time-tracking/${params.row.id}/edit`)}>
            <EditIcon />
          </IconButton>
        )}
        <IconButton onClick={() => handleViewHistory(params.row)}>
          <HistoryIcon />
        </IconButton>
        {canDeleteTimeEntries && (
          <IconButton
            onClick={() => {
              setSelectedEntry(params.row);
              setDeleteDialogOpen(true);
            }}
          >
            <DeleteIcon />
          </IconButton>
        )}
      </Box>
    ),
  };

  const columns: GridColDef[] = [...baseColumns, actionsColumn];

  return (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h5">Zeiterfassung</Typography>
          <Box display="flex" gap={1}>
            <Button
              variant="outlined"
              startIcon={<FilterIcon />}
              onClick={() => setShowFilters(!showFilters)}
              color={getActiveFiltersCount() > 0 ? "primary" : "inherit"}
            >
              Filter {getActiveFiltersCount() > 0 && <Chip size="small" label={getActiveFiltersCount()} sx={{ ml: 1 }} />}
            </Button>
            <Button
              variant="outlined"
              startIcon={<ExportIcon />}
              onClick={handleExport}
            >
              Exportieren
            </Button>
            {canCreateTimeEntries && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => navigate('/time-tracking/new')}
              >
                Neuer Eintrag
              </Button>
            )}
          </Box>
        </Box>

        {showFilters && (
          <Box mb={3} p={2} sx={{ backgroundColor: theme.palette.grey[50], borderRadius: 1 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={3}>
                <TextField
                  select
                  fullWidth
                  label="Projekt"
                  value={filters.project_id}
                  onChange={(e) => handleFilterChange('project_id', e.target.value === '' ? '' : Number(e.target.value))}
                  size="small"
                >
                  <MenuItem value="">Alle Projekte</MenuItem>
                  {projects.map((project) => (
                    <MenuItem key={project.id} value={project.id}>
                      {project.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  select
                  fullWidth
                  label="Mitarbeiter"
                  value={filters.user_id}
                  onChange={(e) => handleFilterChange('user_id', e.target.value === '' ? '' : Number(e.target.value))}
                  size="small"
                >
                  <MenuItem value="">Alle Mitarbeiter</MenuItem>
                  {users.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField
                  type="date"
                  fullWidth
                  label="Von Datum"
                  value={filters.start_date}
                  onChange={(e) => handleFilterChange('start_date', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField
                  type="date"
                  fullWidth
                  label="Bis Datum"
                  value={filters.end_date}
                  onChange={(e) => handleFilterChange('end_date', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <Button
                  variant="outlined"
                  startIcon={<ClearIcon />}
                  onClick={clearFilters}
                  size="small"
                  fullWidth
                >
                  Zurücksetzen
                </Button>
              </Grid>
            </Grid>
          </Box>
        )}
        <DataGrid
          rows={entries}
          columns={columns}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 10, page: 0 },
            },
            sorting: {
              sortModel: [{ field: 'formatted_date', sort: 'desc' }],
            },
          }}
          pageSizeOptions={[10]}
          autoHeight
          disableRowSelectionOnClick
        />
      </CardContent>

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Zeiteintrag löschen"
        message="Sind Sie sicher, dass Sie diesen Zeiteintrag löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden."
        onConfirm={handleDelete}
        onCancel={() => {
          setDeleteDialogOpen(false);
          setSelectedEntry(null);
        }}
        severity="error"
      />

      <Dialog
        open={historyDialogOpen}
        onClose={() => setHistoryDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Änderungshistorie</DialogTitle>
        <DialogContent>
          {historyEntries.map((entry) => (
            <Box key={entry.id} mb={2}>
              <Typography variant="body1">
                {format(new Date(entry.created_at), 'dd.MM.yyyy HH:mm', { locale: de })}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {formatHistoryChange(entry)}
              </Typography>
            </Box>
          ))}
          {historyEntries.length === 0 && (
            <Typography variant="body1">Keine Änderungen gefunden.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryDialogOpen(false)}>Schließen</Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}; 